/**
 * Profile settings — password change, notification preferences, and the
 * (deliberately-friction-heavy) account deletion flow.
 *
 * The three cards are independent so a user can flip a notification toggle
 * without needing to fill any other form. The delete card lives at the
 * bottom in a red-outlined "danger zone" so a misclick can never happen.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, Bell, AlertTriangle, ArrowLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const inputCls =
  "w-full px-3 py-2 rounded-md text-sm border focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent";

const NOTIF_LABELS = {
  report_ready:  { title: "Report ready",         body: "Email me when a paid report finishes generating." },
  credit_expiry: { title: "Credit expiry",        body: "Remind me 30 days before unused credits expire." },
  referral:      { title: "Referral rewards",     body: "Tell me when someone I invited unlocks a report." },
  marketing:     { title: "Product updates",      body: "Occasional tips, changelog highlights, and new features." },
};

export const ProfileSettings = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const isEmailAuth = user?.auth_provider === "email";

  // ── Password ──────────────────────────────────────────
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  // ── Notifications ─────────────────────────────────────
  const [prefs, setPrefs] = useState(null);  // null = loading
  const [prefsSaving, setPrefsSaving] = useState(false);

  // ── Delete account ────────────────────────────────────
  const [delOpen, setDelOpen] = useState(false);
  const [delForm, setDelForm] = useState({ confirm: "", current_password: "" });
  const [delBusy, setDelBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/profile/notifications")
      .then((r) => setPrefs(r.data.notifications))
      .catch(() => toast.error("Could not load preferences"));
  }, [user, loading, navigate]);

  const onSavePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    if (pwd.new_password.length < 8 || !/\d/.test(pwd.new_password)) {
      toast.error("New password must be at least 8 characters and include a number");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/profile/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setPwdSaving(false);
    }
  };

  const onTogglePref = async (key, next) => {
    setPrefsSaving(true);
    // Optimistic — flip locally first so the switch feels instant.
    const prev = prefs;
    setPrefs((p) => ({ ...p, [key]: next }));
    try {
      const { data } = await api.patch("/profile/notifications", { [key]: next });
      setPrefs(data.notifications);
    } catch (err) {
      setPrefs(prev);
      toast.error(err?.response?.data?.detail || "Could not update preference");
    } finally {
      setPrefsSaving(false);
    }
  };

  const onDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete("/profile", { data: delForm });
      toast.success("Account deleted. Sorry to see you go.");
      // Cookie is already cleared by the backend; do a full reload so
      // React state fully resets and AuthContext.user becomes null.
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete account");
      setDelBusy(false);
    }
  };

  if (loading || !user) return (
    <div className="App"><Header /><div className="max-w-[900px] mx-auto px-6 py-10">Loading…</div><Footer /></div>
  );

  return (
    <div className="App">
      <Header />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-10">
        <button
          onClick={() => navigate("/profile")}
          className="text-xs mb-3 inline-flex items-center gap-1.5 hover:opacity-70"
          style={{ color: "var(--muted)" }}
          data-testid="btn-back-profile"
        >
          <ArrowLeft size={12} /> Back to profile
        </button>
        <div className="mb-8">
          <div className="overline mb-2">Account</div>
          <h1 className="font-display text-3xl md:text-4xl">Profile settings</h1>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
            Password, notification preferences, and account controls.
          </p>
        </div>

        {/* Change password */}
        <form onSubmit={onSavePassword} className="card p-6 mb-6" data-testid="card-password">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} style={{ color: "var(--brand-green)" }} />
            <h2 className="font-display text-lg">Change password</h2>
          </div>
          {isEmailAuth ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Current password">
                  <input
                    type="password"
                    className={inputCls}
                    style={{ borderColor: "var(--hairline)" }}
                    value={pwd.current_password}
                    onChange={(e) => setPwd((s) => ({ ...s, current_password: e.target.value }))}
                    data-testid="input-current-password"
                    required
                  />
                </Field>
                <Field label="New password">
                  <input
                    type="password"
                    className={inputCls}
                    style={{ borderColor: "var(--hairline)" }}
                    value={pwd.new_password}
                    onChange={(e) => setPwd((s) => ({ ...s, new_password: e.target.value }))}
                    data-testid="input-new-password"
                    required
                    minLength={8}
                  />
                </Field>
                <Field label="Confirm new password">
                  <input
                    type="password"
                    className={inputCls}
                    style={{ borderColor: "var(--hairline)" }}
                    value={pwd.confirm}
                    onChange={(e) => setPwd((s) => ({ ...s, confirm: e.target.value }))}
                    data-testid="input-confirm-password"
                    required
                  />
                </Field>
              </div>
              <p className="text-xs mt-3" style={{ color: "var(--muted)" }}>
                At least 8 characters, must include a number.
              </p>
              <div className="flex justify-end mt-4">
                <button
                  type="submit"
                  disabled={pwdSaving || !pwd.current_password || !pwd.new_password || !pwd.confirm}
                  className="btn-primary text-sm"
                  data-testid="btn-save-password"
                >
                  {pwdSaving ? "Updating…" : "Update password"}
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="password-not-applicable">
              This account signs in with Google, so there is no password to change here. Manage your Google account security at{" "}
              <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--brand-green)" }}>myaccount.google.com/security</a>.
            </p>
          )}
        </form>

        {/* Notifications */}
        <div className="card p-6 mb-6" data-testid="card-notifications">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={16} style={{ color: "var(--brand-green)" }} />
            <h2 className="font-display text-lg">Notification preferences</h2>
          </div>
          {!prefs ? (
            <div className="text-sm" style={{ color: "var(--muted)" }}>Loading preferences…</div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--hairline)" }}>
              {Object.entries(NOTIF_LABELS).map(([key, meta]) => (
                <div key={key} className="flex items-start justify-between gap-4 py-3" data-testid={`pref-row-${key}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold" style={{ color: "var(--charcoal)" }}>{meta.title}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{meta.body}</div>
                  </div>
                  <Switch
                    checked={!!prefs[key]}
                    disabled={prefsSaving}
                    onCheckedChange={(v) => onTogglePref(key, v)}
                    data-testid={`pref-toggle-${key}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger zone */}
        <div
          className="card p-6"
          data-testid="card-delete-account"
          style={{ border: "1px solid #E5B4A0", background: "#FFF8F4" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} style={{ color: "#873F00" }} />
            <h2 className="font-display text-lg" style={{ color: "#873F00" }}>Delete account</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--charcoal)" }}>
            Permanently removes your account, saved projects, and session. Your unlocked reports remain in our records
            for financial-audit purposes but you will no longer be able to access them. This action cannot be undone.
          </p>
          <button
            onClick={() => setDelOpen(true)}
            className="text-sm px-4 py-2 rounded-md font-semibold"
            style={{ background: "#873F00", color: "white" }}
            data-testid="btn-delete-open"
          >
            Delete my account
          </button>
        </div>
      </div>

      <AlertDialog open={delOpen} onOpenChange={(o) => !delBusy && setDelOpen(o)}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} style={{ color: "#873F00" }} />
              Delete this account?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Type <b>DELETE</b> to confirm. {isEmailAuth ? "Enter your current password to authorise." : "Google-authenticated accounts do not require a password."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <input
              className={inputCls}
              style={{ borderColor: "var(--hairline)" }}
              placeholder="Type DELETE"
              value={delForm.confirm}
              onChange={(e) => setDelForm((s) => ({ ...s, confirm: e.target.value }))}
              data-testid="input-delete-confirm"
              autoFocus
            />
            {isEmailAuth && (
              <input
                type="password"
                className={inputCls}
                style={{ borderColor: "var(--hairline)" }}
                placeholder="Current password"
                value={delForm.current_password}
                onChange={(e) => setDelForm((s) => ({ ...s, current_password: e.target.value }))}
                data-testid="input-delete-password"
              />
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={delBusy} data-testid="btn-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={delBusy || delForm.confirm.trim().toUpperCase() !== "DELETE" || (isEmailAuth && !delForm.current_password)}
              style={{ background: "#873F00" }}
              data-testid="btn-delete-confirm"
            >
              {delBusy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--muted)" }}>{label}</span>
    {children}
  </label>
);

export default ProfileSettings;
